export function isAmbiguousAnnualApyString(value: string): boolean {
  return value.endsWith('.') || value === '.';
}

type RatePerPaymentPeriodInput = {
  annualInterestRate: number;
  /** Number of times interest compounds per year (e.g. 12 for monthly). */
  compoundingPeriodsPerYear: number;
  numberOfPaymentsPerYear: number;
};

/**
 * Effective interest rate per payment period.
 * Formula: rate = ((1+r/n)^(n/p))-1
 * @see https://www.vertex42.com/Calculators/compound-interest-calculator.html#rate-per-period
 */
export function calculateRatePerPaymentPeriod({
  annualInterestRate,
  compoundingPeriodsPerYear,
  numberOfPaymentsPerYear,
}: RatePerPaymentPeriodInput) {
  const ratio = annualInterestRate / compoundingPeriodsPerYear;
  return (1 + ratio) ** (compoundingPeriodsPerYear / numberOfPaymentsPerYear) - 1;
}

type FutureValueOfPrincipalInputs = {
  principal: number;
  ratePerPeriod: number;
  numberOfPeriods: number;
};

/**
 * Future value of a principal: FV = P*(1+rate)^nper
 * @see https://www.vertex42.com/Calculators/compound-interest-calculator.html#formula
 */
export function calculateFutureValueOfPrincipal({
  principal,
  ratePerPeriod,
  numberOfPeriods,
}: FutureValueOfPrincipalInputs) {
  return principal * (1 + ratePerPeriod) ** numberOfPeriods;
}

/**
 * Formats a numeric string for display with grouping; preserves trailing `.` / `.0` while typing.
 * In-progress values like `.` are left as-is so we never render `NaN` from `Number('.')`.
 */
export function formatToDisplayValue(value: string) {
  if (value === '') {
    return value;
  }

  let suffix = '';
  let coreForParse: string;

  if (value.endsWith('.0')) {
    suffix = '.0';
    coreForParse = value.slice(0, -2);
  } else if (value.endsWith('.')) {
    suffix = '.';
    coreForParse = value.slice(0, -1);
  } else {
    coreForParse = value;
  }

  if (coreForParse === '') {
    if (suffix === '.') {
      return '.';
    }
    if (suffix === '.0') {
      return `0${suffix}`;
    }
    return value;
  }

  const n = Number(coreForParse);
  if (!Number.isFinite(n)) {
    return value;
  }

  return `${n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}${suffix}`;
}
